import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  ArrowLeftIcon,
  SparklesIcon,
  TrashIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CheckIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { generateProfessionalPdf } from '../components/documents/PdfExporter';
import { useDiagnosticsStore } from '../stores/diagnosticsStore';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { formatRelativeTime } from '../utils/dateFormat';
import type { MarkdownEditorRef } from '../components/documents/MarkdownEditor';
import clsx from 'clsx';

// Lazy load the heavy MarkdownEditor
const MarkdownEditor = lazy(() =>
  import('../components/documents/MarkdownEditor').then((m) => ({ default: m.MarkdownEditor }))
);

export function DiagnosticAnalysisPage() {
  const { t } = useTranslation('diagnostics');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    selectedForm,
    responses,
    diagnostics,
    isLoading,
    fetchForm,
    fetchResponses,
    fetchDiagnostics,
    generateAnalysis,
    updateDiagnostic,
    deleteDiagnostic,
  } = useDiagnosticsStore();

  const [isGenerating, setIsGenerating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isExporting, setIsExporting] = useState(false);
  const editorRef = useRef<MarkdownEditorRef>(null);

  useEffect(() => {
    if (id) {
      fetchForm(id);
      fetchResponses(id);
      fetchDiagnostics(id);
    }
  }, [id, fetchForm, fetchResponses, fetchDiagnostics]);

  // Auto-expand the first diagnostic if only one exists
  useEffect(() => {
    if (diagnostics.length === 1 && !editingId) {
      setEditingId(diagnostics[0].id);
      setEditContent(diagnostics[0].content);
    }
  }, [diagnostics, editingId]);

  const handleGenerate = async () => {
    if (!id) return;

    setIsGenerating(true);
    try {
      await generateAnalysis(id, {});
      await fetchDiagnostics(id);
    } catch (error) {
      console.error('Error generating analysis:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEdit = (diagnosticId: string, content: string) => {
    setEditingId(diagnosticId);
    setEditContent(content);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    if (!id || !editingId) return;

    setIsSaving(true);
    setSaveStatus('saving');
    try {
      await updateDiagnostic(id, editingId, { content: editContent });
      await fetchDiagnostics(id);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving diagnostic:', error);
      setSaveStatus('idle');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (diagnosticId: string) => {
    if (!id) return;

    try {
      await updateDiagnostic(id, diagnosticId, { status: 'published' });
      await fetchDiagnostics(id);
    } catch (error) {
      console.error('Error publishing diagnostic:', error);
    }
  };

  const handleDelete = async (diagnosticId: string) => {
    if (!id) return;
    try {
      await deleteDiagnostic(id, diagnosticId);
      if (editingId === diagnosticId) {
        setEditingId(null);
        setEditContent('');
      }
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting diagnostic:', error);
    }
  };

  const handleExportPdf = async () => {
    if (!editingId || !editContent) return;

    const diagnostic = diagnostics.find(d => d.id === editingId);
    if (!diagnostic) return;

    setIsExporting(true);
    try {
      const title = diagnostic.title || selectedForm?.title || 'Diagnóstico';
      await generateProfessionalPdf({
        title,
        markdown: editContent,
        documentType: 'diagnostic',
        locale: 'es',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading || !selectedForm) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  const completedResponses = responses.filter(r => r.status === 'completed');
  const canGenerate = completedResponses.length > 0;
  const editingDiagnostic = diagnostics.find(d => d.id === editingId);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={() => navigate(`/diagnostics/${id}`)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {t('analysis.title')}
            </h1>
            <p className="text-gray-500 dark:text-slate-400 truncate text-sm sm:text-base">{selectedForm.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
          {/* Save status */}
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-500 dark:text-slate-400 flex items-center gap-1">
              <Spinner size="sm" />
              Guardando...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckIcon className="w-4 h-4" />
              Guardado
            </span>
          )}

          {editingId && editingDiagnostic && (
            <>
              <button
                onClick={handleExportPdf}
                disabled={isExporting}
                className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50"
                title="Exportar PDF"
              >
                {isExporting ? (
                  <Spinner size="sm" />
                ) : (
                  <ArrowDownTrayIcon className="w-4 h-4" />
                )}
              </button>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={isSaving}
              >
                Guardar
              </Button>
              {editingDiagnostic.status === 'draft' && (
                <Button
                  variant="secondary"
                  onClick={() => handlePublish(editingId)}
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Publicar
                </Button>
              )}
            </>
          )}

          <Button
            onClick={handleGenerate}
            disabled={!canGenerate || isGenerating}
          >
            {isGenerating ? (
              <>
                <Spinner size="sm" />
                {t('analysis.generating')}
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4" />
                {t('analysis.generate')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* No responses warning */}
      {!canGenerate && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {t('analysis.noResponses')}
          </p>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row gap-4 lg:gap-6 min-h-0">
        {/* Diagnostics list sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-2 overflow-y-auto max-h-48 lg:max-h-none">
          {diagnostics.length === 0 ? (
            <div className="text-center py-8">
              <ChartBarIcon className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {t('analysis.empty')}
              </p>
            </div>
          ) : (
            diagnostics.map((diagnostic) => (
              <motion.div
                key={diagnostic.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => handleEdit(diagnostic.id, diagnostic.content)}
                className={clsx(
                  'p-3 rounded-lg cursor-pointer transition-all border',
                  editingId === diagnostic.id
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-500'
                    : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex items-start gap-2">
                  <DocumentTextIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {diagnostic.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      {formatRelativeTime(diagnostic.createdAt)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {diagnostic.maturityScore && (
                        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                          {typeof diagnostic.maturityScore === 'object' && 'overall' in diagnostic.maturityScore
                            ? `${(diagnostic.maturityScore as { overall: number }).overall}/5`
                            : '--'}
                        </span>
                      )}
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-xs font-medium',
                          diagnostic.status === 'published'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                        )}
                      >
                        {diagnostic.status === 'published' ? 'Publicado' : 'Borrador'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(diagnostic.id);
                    }}
                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Editor area */}
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col min-h-0">
          {editingId ? (
            <Suspense
              fallback={
                <div className="flex-1 flex items-center justify-center">
                  <Spinner size="lg" />
                </div>
              }
            >
              <div className="flex-1 overflow-hidden">
                <MarkdownEditor
                  ref={editorRef}
                  content={editContent}
                  onChange={(content) => {
                    setEditContent(content);
                    setSaveStatus('idle');
                  }}
                  className="h-full"
                />
              </div>
            </Suspense>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <DocumentTextIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-400">
                  {diagnostics.length > 0
                    ? 'Selecciona un diagnóstico para ver o editar'
                    : t('analysis.emptyDescription')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('analysis.delete')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-slate-400">
            {t('analysis.confirmDelete')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
